import { useEffect, useState, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import NrcsPracticePicker from '../ui/NrcsPracticePicker';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import {
  FileText, Leaf, Building2, Download, Loader2, Plus, Trash2, Edit2,
  ShieldCheck, FileSpreadsheet, Archive, Copy, Lock, Calendar,
} from 'lucide-react';
import { toast } from 'sonner';
import { todayLocal } from '../../utils/dates';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SECTION_LABELS = {
  usda_identifiers: 'USDA Identifiers',
  conservation: 'Conservation Practices',
  inventory: 'Livestock & Poultry Inventory',
  year_summary: 'Year in Numbers (annual summary)',
  financial_summary: 'Financial Summary (loan)',
  production: 'Production Logs (eggs, milk, weight)',
  breeding: 'Breeding & Incubation Records',
  health: 'Health & Veterinary Records',
  sustainability: 'Sustainability Metrics',
  notes: 'Operator Notes',
  photos: 'Photo Documentation',
  market: 'Market & Profit Projections',
};

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'planned', label: 'Planned' },
  { value: 'completed', label: 'Completed' },
];

const ReportsPage = () => {
  const { getAuthHeader } = useAuth();
  const [searchParams] = useSearchParams();

  // Farm profile state
  const [profile, setProfile] = useState({
    legal_entity_name: '', dba_llc_names: '', usda_farm_number: '',
    fsa_tract_number: '', uei: '', duns: '',
    address: '', phone: '', contact_email: '',
  });
  const [einInput, setEinInput] = useState('');
  const [einMasked, setEinMasked] = useState(null);
  const [einSet, setEinSet] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);

  // Conservation practices state
  const [practices, setPractices] = useState([]);
  const [editingPractice, setEditingPractice] = useState(null);
  const [practiceForm, setPracticeForm] = useState({
    practice_name: '', nrcs_code: '', unit: '', acres: '', description: '',
    start_year: new Date().getFullYear(), status: 'active',
  });
  const [savingPractice, setSavingPractice] = useState(false);
  const [nrcsPractices, setNrcsPractices] = useState([]);

  // Templates + generation state
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('grant_application');
  const [sectionToggles, setSectionToggles] = useState({});
  const [generating, setGenerating] = useState(false);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'generate');

  // Date range state (optional)
  const [dateRangeStart, setDateRangeStart] = useState('');
  const [dateRangeEnd, setDateRangeEnd] = useState('');

  // Disclaimer modal state
  const [showDisclaimer, setShowDisclaimer] = useState(false);

  // Snapshots state
  const [snapshots, setSnapshots] = useState([]);
  const [savingSnapshot, setSavingSnapshot] = useState(false);
  const [snapshotName, setSnapshotName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  const loadAll = useCallback(async () => {
    try {
      const [pRes, cRes, tRes, sRes, nRes] = await Promise.all([
        axios.get(`${API_URL}/api/farm-profile`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/conservation-practices`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/reports/templates`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/reports/snapshots`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/nrcs-practices`, { headers: getAuthHeader() }).catch(() => ({ data: { practices: [] } })),
      ]);
      const p = pRes.data || {};
      setProfile({
        legal_entity_name: p.legal_entity_name || '',
        dba_llc_names: p.dba_llc_names || '',
        usda_farm_number: p.usda_farm_number || '',
        fsa_tract_number: p.fsa_tract_number || '',
        uei: p.uei || '',
        duns: p.duns || '',
        address: p.address || '',
        phone: p.phone || '',
        contact_email: p.contact_email || '',
      });
      setEinMasked(p.ein_masked);
      setEinSet(!!p.ein_set);
      setPractices(cRes.data.practices || []);
      setTemplates(tRes.data.templates || []);
      setSnapshots(sRes.data.snapshots || []);
      setNrcsPractices(nRes.data.practices || []);
      // Initialise section toggles from URL param or default
      const tplId = searchParams.get('template') || 'grant_application';
      const tpl = (tRes.data.templates || []).find((t) => t.id === tplId);
      if (tpl) {
        setSelectedTemplate(tpl.id);
        // Honor preselect params from deep-links (?include=inventory,sustainability)
        const include = (searchParams.get('include') || '').split(',').filter(Boolean);
        const next = { ...tpl.default_sections };
        include.forEach((s) => { next[s] = true; });
        setSectionToggles(next);
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to load reports data');
    }
  }, [getAuthHeader, searchParams]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ----- Farm profile handlers -----
  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      const payload = { ...profile };
      if (einInput) payload.ein = einInput;
      const res = await axios.put(`${API_URL}/api/farm-profile`, payload, { headers: getAuthHeader() });
      setEinMasked(res.data.ein_masked);
      setEinSet(!!res.data.ein_set);
      setEinInput('');
      toast.success('Farm profile saved');
    } catch (err) {
      console.error(err);
      toast.error('Save failed');
    } finally {
      setSavingProfile(false);
    }
  };

  // ----- Conservation practice handlers -----
  const resetPracticeForm = () => {
    setPracticeForm({
      practice_name: '', nrcs_code: '', unit: '', acres: '', description: '',
      start_year: new Date().getFullYear(), status: 'active',
    });
    setEditingPractice(null);
  };

  const savePractice = async () => {
    if (!practiceForm.nrcs_code || !practiceForm.practice_name) {
      toast.error('Pick an NRCS practice from the list');
      return;
    }
    setSavingPractice(true);
    try {
      const body = {
        ...practiceForm,
        acres: practiceForm.acres === '' ? null : Number(practiceForm.acres),
        start_year: practiceForm.start_year ? Number(practiceForm.start_year) : null,
      };
      if (editingPractice) {
        await axios.put(`${API_URL}/api/conservation-practices/${editingPractice.id}`, body, { headers: getAuthHeader() });
        toast.success('Practice updated');
      } else {
        await axios.post(`${API_URL}/api/conservation-practices`, body, { headers: getAuthHeader() });
        toast.success('Practice added');
      }
      resetPracticeForm();
      const cRes = await axios.get(`${API_URL}/api/conservation-practices`, { headers: getAuthHeader() });
      setPractices(cRes.data.practices || []);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSavingPractice(false);
    }
  };

  const editPractice = (p) => {
    setEditingPractice(p);
    setPracticeForm({
      practice_name: p.practice_name || '',
      nrcs_code: p.nrcs_code || '',
      unit: p.unit || '',
      acres: p.acres ?? '',
      description: p.description || '',
      start_year: p.start_year || new Date().getFullYear(),
      status: p.status || 'active',
    });
  };

  const deletePractice = async (id) => {
    if (!window.confirm('Delete this conservation practice?')) return;
    try {
      await axios.delete(`${API_URL}/api/conservation-practices/${id}`, { headers: getAuthHeader() });
      setPractices((prev) => prev.filter((p) => p.id !== id));
      toast.success('Practice deleted');
    } catch (err) {
      console.error(err);
      toast.error('Delete failed');
    }
  };

  // ----- Generation -----
  const onTemplateChange = (id) => {
    setSelectedTemplate(id);
    const tpl = templates.find((t) => t.id === id);
    if (tpl) setSectionToggles({ ...tpl.default_sections });
  };

  const toggleSection = (id, checked) => {
    setSectionToggles((prev) => ({ ...prev, [id]: !!checked }));
  };

  const generateReport = async (output_format) => {
    setGenerating(true);
    try {
      const sections = { ...sectionToggles };
      if (dateRangeStart) sections.date_range_start = dateRangeStart;
      if (dateRangeEnd) sections.date_range_end = dateRangeEnd;
      const res = await axios.post(
        `${API_URL}/api/reports/generate`,
        { template: selectedTemplate, sections, output_format },
        { headers: getAuthHeader(), responseType: 'blob' },
      );
      const ext = output_format === 'csv' ? 'csv' : 'pdf';
      const blob = new Blob([res.data], { type: output_format === 'csv' ? 'text/csv' : 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fencepost_${selectedTemplate}_${todayLocal()}.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`${ext.toUpperCase()} downloaded`);
    } catch (err) {
      console.error(err);
      toast.error('Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  // ----- Snapshots -----
  const refreshSnapshots = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/reports/snapshots`, { headers: getAuthHeader() });
      setSnapshots(res.data.snapshots || []);
    } catch (err) {
      console.error(err);
    }
  };

  const saveSnapshot = async () => {
    if (savingSnapshot) return;  // guard against double-fire (StrictMode)
    if (!snapshotName.trim()) {
      toast.error('Give your snapshot a name');
      return;
    }
    setSavingSnapshot(true);
    try {
      await axios.post(`${API_URL}/api/reports/snapshots`, {
        name: snapshotName.trim(),
        template: selectedTemplate,
        sections: sectionToggles,
        date_range_start: dateRangeStart || null,
        date_range_end: dateRangeEnd || null,
      }, { headers: getAuthHeader() });
      toast.success('Snapshot saved — immutable audit copy created');
      setSnapshotName('');
      setShowSaveDialog(false);
      await refreshSnapshots();
      setActiveTab('snapshots');
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSavingSnapshot(false);
    }
  };

  const downloadSnapshot = async (snap, fmt) => {
    setDownloadingId(`${snap.id}-${fmt}`);
    try {
      const res = await axios.get(
        `${API_URL}/api/reports/snapshots/${snap.id}/download?format=${fmt}`,
        { headers: getAuthHeader(), responseType: 'blob' },
      );
      const blob = new Blob([res.data], { type: fmt === 'csv' ? 'text/csv' : 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safe = (snap.name || 'snapshot').replace(/[^a-zA-Z0-9._-]/g, '_');
      a.download = `${safe}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      toast.error('Download failed');
    } finally {
      setDownloadingId(null);
    }
  };

  const duplicateSnapshot = (snap) => {
    // Pre-populate the Generate tab with this snapshot's selection state.
    // Snapshot itself remains immutable; the user generates a fresh report.
    setSelectedTemplate(snap.template);
    const next = { ...(snap.selection_state || {}) };
    delete next.date_range_start;
    delete next.date_range_end;
    setSectionToggles(next);
    setDateRangeStart(snap.date_range_start || '');
    setDateRangeEnd(snap.date_range_end || '');
    setSnapshotName(`${snap.name} (copy)`);
    setActiveTab('generate');
    toast.success('Selection state loaded — adjust and save as new snapshot');
  };

  const deleteSnapshot = async (snap) => {
    if (!window.confirm(`Delete snapshot "${snap.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/api/reports/snapshots/${snap.id}`, { headers: getAuthHeader() });
      setSnapshots((prev) => prev.filter((s) => s.id !== snap.id));
      toast.success('Snapshot deleted');
    } catch (err) {
      console.error(err);
      toast.error('Delete failed');
    }
  };

  const currentTemplate = templates.find((t) => t.id === selectedTemplate);
  const profileReady = !!(profile.legal_entity_name && profile.usda_farm_number);

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold">Reports & Documentation</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Generate grant applications, loan packets, annual operations reports, and buyer/vet documentation
          straight from your farm records.
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid grid-cols-4 mb-4">
          <TabsTrigger value="generate" data-testid="reports-tab-generate">
            <FileText className="w-4 h-4 mr-2" />
            Generate
          </TabsTrigger>
          <TabsTrigger value="snapshots" data-testid="reports-tab-snapshots">
            <Archive className="w-4 h-4 mr-2" />
            Snapshots
            {snapshots.length > 0 && (
              <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-primary/15 text-primary">
                {snapshots.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="profile" data-testid="reports-tab-profile">
            <Building2 className="w-4 h-4 mr-2" />
            Farm Profile
          </TabsTrigger>
          <TabsTrigger value="conservation" data-testid="reports-tab-conservation">
            <Leaf className="w-4 h-4 mr-2" />
            Conservation
          </TabsTrigger>
        </TabsList>

        {/* ===== GENERATE TAB ===== */}
        <TabsContent value="generate" className="space-y-4">
          {!profileReady && (
            <Card className="card-farm border-amber-300 bg-amber-50/60">
              <CardContent className="py-4 text-sm">
                <strong className="text-amber-800">Farm profile incomplete.</strong>{' '}
                <span className="text-amber-900/80">
                  Reviewers expect at least a legal entity name and USDA farm number on the cover page.
                </span>{' '}
                <button onClick={() => setActiveTab('profile')} className="underline text-amber-900">
                  Fill it in →
                </button>
              </CardContent>
            </Card>
          )}

          <Card className="card-farm">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-lg">1. Choose a template</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {templates.map((t) => {
                  const active = t.id === selectedTemplate;
                  return (
                    <button
                      key={t.id}
                      onClick={() => onTemplateChange(t.id)}
                      className={`text-left p-3 rounded-lg border transition-colors ${
                        active
                          ? 'border-primary bg-primary/5'
                          : 'border-muted-foreground/20 hover:border-primary/50'
                      }`}
                      data-testid={`template-${t.id}`}
                    >
                      <div className="font-medium text-sm">{t.label}</div>
                      <div className="text-xs text-muted-foreground mt-1">{t.description}</div>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          <Card className="card-farm">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-lg">2. Select sections to include</CardTitle>
              {currentTemplate && (
                <p className="text-xs text-muted-foreground">
                  Defaults shown for <strong>{currentTemplate.label}</strong>. Uncheck anything you don&apos;t want.
                </p>
              )}
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {Object.entries(SECTION_LABELS).map(([id, label]) => (
                  <label
                    key={id}
                    className="flex items-center gap-2 p-2 rounded-md hover:bg-muted/30 cursor-pointer"
                  >
                    <Checkbox
                      checked={!!sectionToggles[id]}
                      onCheckedChange={(c) => toggleSection(id, c)}
                      data-testid={`section-toggle-${id}`}
                    />
                    <span className="text-sm">{label}</span>
                  </label>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="card-farm">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Calendar className="w-4 h-4 text-primary" />
                Date range <span className="text-xs font-normal text-muted-foreground">(optional)</span>
              </CardTitle>
              <p className="text-xs text-muted-foreground">
                Limits production logs, breeding events, and health records to this window.
                Leave blank to include the last 12 months of each.
              </p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="date-start">From</Label>
                  <Input
                    id="date-start"
                    type="date"
                    value={dateRangeStart}
                    onChange={(e) => setDateRangeStart(e.target.value)}
                    data-testid="date-range-start"
                  />
                </div>
                <div>
                  <Label htmlFor="date-end">To</Label>
                  <Input
                    id="date-end"
                    type="date"
                    value={dateRangeEnd}
                    onChange={(e) => setDateRangeEnd(e.target.value)}
                    data-testid="date-range-end"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-farm">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-lg">4. Download or save snapshot</CardTitle>
              <p className="text-xs text-muted-foreground">
                Downloads give you the file right now. Snapshots also save an immutable
                audit copy you can re-download or duplicate later.
              </p>
            </CardHeader>
            <CardContent>
              <div
                className="mb-4 p-3 rounded-md border border-amber-200 bg-amber-50/60 text-xs text-amber-900 leading-relaxed"
                data-testid="pre-submit-disclaimer"
                role="note"
              >
                <strong className="font-semibold">Before you submit:</strong>{' '}
                review every section for accuracy. Fencepost compiles the records you&apos;ve
                entered, but it does not verify them. You are responsible for the content of
                your application and any decisions made from it.
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={() => generateReport('pdf')}
                  disabled={generating}
                  className="btn-primary"
                  data-testid="generate-pdf-btn"
                >
                  {generating ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  Download PDF
                </Button>
                <Button
                  variant="outline"
                  onClick={() => generateReport('csv')}
                  disabled={generating}
                  data-testid="generate-csv-btn"
                >
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  Download CSV
                </Button>
                <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
                  <DialogTrigger asChild>
                    <Button variant="secondary" data-testid="open-save-snapshot-btn">
                      <Archive className="w-4 h-4 mr-2" />
                      Save as snapshot
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Save an immutable snapshot</DialogTitle>
                      <DialogDescription>
                        Locks today&apos;s PDF, CSV, and selection state into a permanent
                        audit record. Snapshots cannot be edited — only duplicated or deleted.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                      <p className="text-xs text-muted-foreground">
                        Use the snapshot for audit trails, or &quot;Duplicate&quot; it later to
                        start a new application 80% prefilled.
                      </p>
                      <div>
                        <Label htmlFor="snapshot-name">Snapshot name</Label>
                        <Input
                          id="snapshot-name"
                          value={snapshotName}
                          onChange={(e) => setSnapshotName(e.target.value)}
                          placeholder="EQIP App – Q2 2026"
                          data-testid="snapshot-name-input"
                        />
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Template: <strong>{currentTemplate?.label || selectedTemplate}</strong>
                        {(dateRangeStart || dateRangeEnd) && (
                          <span className="ml-2">
                            · Date range: {dateRangeStart || '…'} → {dateRangeEnd || '…'}
                          </span>
                        )}
                      </div>
                      <Button
                        onClick={saveSnapshot}
                        disabled={savingSnapshot}
                        className="w-full btn-primary"
                        data-testid="confirm-save-snapshot-btn"
                      >
                        {savingSnapshot ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Lock className="w-4 h-4 mr-2" />
                        )}
                        Create snapshot
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== SNAPSHOTS TAB ===== */}
        <TabsContent value="snapshots" className="space-y-4">
          <Card className="card-farm">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Lock className="w-5 h-5 text-primary" />
                Saved Snapshots
              </CardTitle>
              <p className="text-xs text-muted-foreground">
                Immutable records of past reports — perfect for audit trails and
                duplicate-and-tweak workflows. Snapshots cannot be edited; create
                a new one to capture updated farm data.
              </p>
            </CardHeader>
            <CardContent>
              {snapshots.length === 0 ? (
                <div className="text-center py-8 text-sm text-muted-foreground">
                  No snapshots yet. Generate a report and click <strong>Save as snapshot</strong> to lock it in.
                </div>
              ) : (
                <div className="space-y-2">
                  {snapshots.map((s) => (
                    <div
                      key={s.id}
                      className="flex items-start justify-between gap-3 p-3 rounded-lg border bg-muted/20"
                      data-testid={`snapshot-${s.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-sm">{s.name}</span>
                          <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-primary/10 text-primary">
                            {s.template_label}
                          </span>
                          <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 inline-flex items-center gap-1">
                            <Lock className="w-2.5 h-2.5" />
                            Immutable
                          </span>
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          {new Date(s.created_at).toLocaleString()}
                          {(s.date_range_start || s.date_range_end) && (
                            <span className="ml-2">
                              · {s.date_range_start || '…'} → {s.date_range_end || '…'}
                            </span>
                          )}
                          {s.farm_profile_snapshot?.legal_entity_name && (
                            <span className="ml-2">
                              · {s.farm_profile_snapshot.legal_entity_name}
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] text-muted-foreground mt-0.5">
                          PDF {(s.pdf_bytes / 1024).toFixed(1)} KB · CSV {(s.csv_bytes / 1024).toFixed(1)} KB
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1 justify-end">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => downloadSnapshot(s, 'pdf')}
                          disabled={downloadingId === `${s.id}-pdf`}
                          data-testid={`snapshot-download-pdf-${s.id}`}
                        >
                          {downloadingId === `${s.id}-pdf` ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <Download className="w-3 h-3" />
                          )}
                          <span className="ml-1 text-xs">PDF</span>
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => downloadSnapshot(s, 'csv')}
                          disabled={downloadingId === `${s.id}-csv`}
                          data-testid={`snapshot-download-csv-${s.id}`}
                        >
                          <FileSpreadsheet className="w-3 h-3" />
                          <span className="ml-1 text-xs">CSV</span>
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => duplicateSnapshot(s)}
                          data-testid={`snapshot-duplicate-${s.id}`}
                          title="Duplicate this snapshot's selection into the Generate tab"
                        >
                          <Copy className="w-3 h-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteSnapshot(s)}
                          data-testid={`snapshot-delete-${s.id}`}
                        >
                          <Trash2 className="w-3 h-3 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== PROFILE TAB (read-only summary — canonical editor moved to Settings > Grant & Federal Identifiers, Feb 2026) ===== */}
        <TabsContent value="profile" className="space-y-4">
          <Card className="card-farm" data-testid="reports-profile-readonly">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Building2 className="w-5 h-5 text-primary" />
                Farm Profile — Read-Only
              </CardTitle>
              <p className="text-xs text-muted-foreground">
                These identifiers prefill the cover page of every report. To edit, use{' '}
                <Link to="/app/settings" className="text-primary font-semibold underline underline-offset-2"
                  data-testid="edit-profile-in-settings-link">
                  Settings → Grant &amp; Federal Identifiers
                </Link>{' '}so you never have to enter the same values twice.
              </p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                {[
                  ['Legal Entity Name', profile.legal_entity_name],
                  ['DBA / LLC Names', profile.dba_llc_names],
                  ['FSA Farm Serial #', profile.usda_farm_number],
                  ['FSA Tract #', profile.fsa_tract_number],
                  ['UEI (SAM.gov)', profile.uei],
                  ['DUNS (legacy)', profile.duns],
                  ['EIN / Tax ID', einSet ? (einMasked || '••••••') : ''],
                  ['Mailing Address', profile.address],
                  ['Phone', profile.phone],
                  ['Contact Email', profile.contact_email],
                ].map(([label, val]) => (
                  <div key={label} className="border-b border-border/50 py-2">
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
                    <div className="font-medium text-foreground/90 min-h-[20px]">
                      {val || <span className="text-muted-foreground italic">Not set</span>}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex justify-end">
                <Button asChild variant="outline" data-testid="edit-profile-cta">
                  <Link to="/app/settings">Edit in Settings →</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== CONSERVATION TAB ===== */}
        <TabsContent value="conservation" className="space-y-4">
          <Card className="card-farm">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Leaf className="w-5 h-5 text-primary" />
                Conservation Practices
              </CardTitle>
              <p className="text-xs text-muted-foreground">
                Track NRCS conservation practices on your operation (e.g., 327 Conservation Cover,
                386 Field Border, 590 Nutrient Management). Required for EQIP/CSP/SARE applications.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <Label htmlFor="nrcs_practice_picker">NRCS Practice *</Label>
                  <NrcsPracticePicker
                    practices={nrcsPractices}
                    code={practiceForm.nrcs_code}
                    name={practiceForm.practice_name}
                    onSelect={(p) => setPracticeForm({
                      ...practiceForm,
                      nrcs_code: p.code,
                      practice_name: p.name,
                      unit: p.unit,
                    })}
                  />
                  <p className="text-[11px] text-muted-foreground mt-1">
                    Searches the official NRCS Conservation Practice Standards list ({nrcsPractices.length} practices).
                    On the PDF this renders as "{practiceForm.nrcs_code || 'CODE'} — {practiceForm.practice_name || 'Practice Name'}".
                  </p>
                </div>
                <div>
                  <Label htmlFor="acres">Extent{practiceForm.unit ? ` (${practiceForm.unit})` : ''}</Label>
                  <Input
                    id="acres"
                    type="number"
                    step="0.1"
                    value={practiceForm.acres}
                    onChange={(e) => setPracticeForm({ ...practiceForm, acres: e.target.value })}
                    placeholder={practiceForm.unit ? `e.g. 3.2 ${practiceForm.unit}` : '3.2'}
                    data-testid="practice-acres-input"
                  />
                </div>
                <div>
                  <Label htmlFor="start_year">Start Year</Label>
                  <Input
                    id="start_year"
                    type="number"
                    value={practiceForm.start_year}
                    onChange={(e) => setPracticeForm({ ...practiceForm, start_year: e.target.value })}
                    data-testid="practice-year-input"
                  />
                </div>
                <div>
                  <Label htmlFor="practice_status">Status</Label>
                  <Select
                    value={practiceForm.status}
                    onValueChange={(v) => setPracticeForm({ ...practiceForm, status: v })}
                  >
                    <SelectTrigger data-testid="practice-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map((s) => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={practiceForm.description}
                    onChange={(e) => setPracticeForm({ ...practiceForm, description: e.target.value })}
                    placeholder="Brief description for grant reviewers"
                    rows={3}
                    data-testid="practice-description-input"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={savePractice}
                  disabled={savingPractice}
                  className="btn-primary"
                  data-testid="save-practice-btn"
                >
                  {savingPractice ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                  {editingPractice ? 'Update Practice' : 'Add Practice'}
                </Button>
                {editingPractice && (
                  <Button variant="outline" onClick={resetPracticeForm}>Cancel</Button>
                )}
              </div>
            </CardContent>
          </Card>

          {practices.length > 0 && (
            <Card className="card-farm">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-base">Recorded Practices ({practices.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {practices.map((p) => (
                    <div
                      key={p.id}
                      className="flex items-start justify-between gap-2 p-3 rounded-lg border bg-muted/20"
                      data-testid={`practice-${p.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-mono px-1.5 py-0.5 rounded bg-primary/10 text-primary">
                            {p.nrcs_code || '—'}
                          </span>
                          <span className="font-medium text-sm">— {p.practice_name}</span>
                          {p.acres != null && (
                            <span className="text-xs text-muted-foreground">{p.acres} {p.unit || 'ac'}</span>
                          )}
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                            p.status === 'active' ? 'bg-green-100 text-green-700' :
                            p.status === 'planned' ? 'bg-amber-100 text-amber-700' :
                            'bg-muted text-muted-foreground'
                          }`}>
                            {p.status}
                          </span>
                        </div>
                        {p.description && (
                          <p className="text-xs text-muted-foreground mt-1">{p.description}</p>
                        )}
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => editPractice(p)}>
                          <Edit2 className="w-3 h-3" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => deletePractice(p.id)}>
                          <Trash2 className="w-3 h-3 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Footer disclaimer link — small, subdued, available from every tab */}
      <div className="pt-6 border-t border-muted-foreground/15 flex items-center justify-center">
        <button
          type="button"
          onClick={() => setShowDisclaimer(true)}
          className="text-[11px] text-muted-foreground hover:text-foreground hover:underline transition-colors"
          data-testid="open-disclaimer-link"
        >
          Disclaimer
        </button>
      </div>

      {/* Long-form disclaimer modal */}
      <Dialog open={showDisclaimer} onOpenChange={setShowDisclaimer}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="disclaimer-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">About Fencepost Reports</DialogTitle>
            <DialogDescription className="sr-only">
              Disclaimer covering the scope and limitations of Fencepost Reports.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 text-sm leading-relaxed text-foreground/90">
            <p>
              Fencepost Reports compile data you have entered into the app into formatted
              PDF and CSV documents. These reports are tools to help you organize your
              operational records for grant applications, loan applications, buyer
              documentation, veterinary records, and personal archives.
            </p>

            <div>
              <p className="font-semibold mb-1">Fencepost does not:</p>
              <ul className="list-disc list-outside pl-5 space-y-1 text-muted-foreground">
                <li>Guarantee that any report will result in grant approval, loan approval, or any specific outcome</li>
                <li>Verify the accuracy of the data you have entered</li>
                <li>Provide grant writing, financial, legal, tax, or veterinary advice</li>
                <li>Submit reports to any agency, lender, or institution on your behalf</li>
              </ul>
            </div>

            <div>
              <p className="font-semibold mb-1">You are responsible for:</p>
              <ul className="list-disc list-outside pl-5 space-y-1 text-muted-foreground">
                <li>The accuracy and completeness of the data in your Fencepost account</li>
                <li>The content and submission of any application or document you create using these reports</li>
                <li>Understanding the specific requirements of any grant program, lender, or agency you are working with</li>
                <li>Any decisions made on the basis of these reports</li>
              </ul>
            </div>

            <p>
              Fencepost provides these tools as-is to support your operation. Application
              outcomes depend on many factors outside the scope of this software.
            </p>

            <p className="text-xs text-muted-foreground border-t pt-3">
              To the fullest extent permitted by law, Fencepost is not liable for any loss,
              damage, missed opportunity, or denied application arising from the use of
              these reports, including any inaccuracy in the data they contain.
            </p>
          </div>
          <div className="flex justify-end pt-2">
            <Button
              variant="outline"
              onClick={() => setShowDisclaimer(false)}
              data-testid="close-disclaimer-btn"
            >
              Got it
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReportsPage;
